/**
 * Target label rows of the RENO time series (RenoTimeSeries.vue).
 *
 * Kept free of Vue so the row set and its filter are unit-tested as they ship.
 */

/** Label row definition mapping API target key to i18n key. */
export interface LabelRowDef {
  key: string;
  i18nKey: string;
}

/** Minimal trend-entry shape the filter reads. */
export interface TargetConfidenceCarrier {
  targetConfidence?: Record<string, unknown> | null;
}

const row = (key: string): LabelRowDef => ({ key, i18nKey: `quality.timeSeries.targets.${key}` });

/**
 * Every target any program group predicts, grouped by theme. Groups are rendered
 * with a small vertical gap between them; each row has its own color (see the
 * timeline track colors in `colors.ts`).
 *
 * The program groups predict different targets (see
 * `docs/devices/ai-predictions/overview.md`), so this is a superset and
 * `visibleLabelGroups` keeps only the rows a drying's trend carries. EDT
 * (programs 0/1/2) and raumtrocknung (program 8) share only `powerOutage`, so
 * they never show the drying-state and room-climate groups side by side.
 */
export const LABEL_GROUPS: LabelRowDef[][] = [
  // Drying state (EDT, monitoring)
  [row("dry"), row("stationary"), row("newMoistureIngress")],
  // Slow-drying causes (EDT, monitoring)
  [row("slowDrying"), row("unfavorableAmbient"), row("lowAirflow"), row("peripheralMoisture")],
  // Room climate (raumtrocknung). Binary findings: out of the good range, with no
  // direction, so the labels name the measure only.
  [row("roomTemperature"), row("roomHumidity"), row("moistureLoad"), row("deviceOutput")],
  // Device faults (`powerOutage` is shared by every program group)
  [row("powerOutage"), row("relayDefect")],
  // A hint, not a problem: outdoor air dry enough that ventilating would remove
  // moisture (raumtrocknung). Last and in a calm color, so it never reads as an
  // alarm. EDT's `lowInitialMoisture` is deliberately absent (no longer relevant,
  // Taimoor 2026-10-02), so its key is ignored like any unknown one.
  [row("favorableWeather")],
];

/**
 * The label rows to render, restricted to targets the trend data actually
 * carries (on any day).
 *
 * The backend pre-fills every target a program group predicts with `"none"`,
 * so a key's presence means "this target was evaluated". A rendered row with no
 * confidence anywhere along it reads as "this never occurred", which for a
 * target that was never evaluated is a claim the data does not support. Dropping
 * the row says nothing instead. Keys outside `LABEL_GROUPS` are ignored.
 */
export function visibleLabelGroups(entries: TargetConfidenceCarrier[]): LabelRowDef[][] {
  if (entries.length === 0) return LABEL_GROUPS;

  const predictedTargets = new Set<string>();
  for (const entry of entries) {
    for (const key of Object.keys(entry.targetConfidence ?? {})) {
      predictedTargets.add(key);
    }
  }

  return LABEL_GROUPS.map((group) =>
    group.filter((labelRow) => predictedTargets.has(labelRow.key))
  ).filter((group) => group.length > 0);
}

/** Day-label steps for the strip's day axis: every day, every 2nd, 5th, week, 10th, 2 weeks, 30th … */
const DAY_LABEL_STEPS = [1, 2, 5, 7, 10, 14, 30, 60, 90];

/**
 * The step between labelled days on the strip's day axis: the smallest one whose labels stay at
 * least `minSpacing` px apart. On a long drying a label per day overlapped (53 days on a phone).
 */
export function dayLabelStep(pixelsPerDay: number, minSpacing: number): number {
  if (!(pixelsPerDay > 0)) {
    return 1;
  }

  return DAY_LABEL_STEPS.find((step) => step * pixelsPerDay >= minSpacing) ?? DAY_LABEL_STEPS[DAY_LABEL_STEPS.length - 1];
}

